import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react'
import { User, onAuthStateChanged, signInWithCustomToken, signOut } from 'firebase/auth'
import { initializeFirebase, getFirebaseAuth } from '@/lib/firebase'
import { api } from '@/api/baseApi'

interface MemberInfo {
  firstName: string
  lastName: string
  phoneNumber: string
}

interface AuthContextType {
  user: User | null
  loading: boolean
  isAffiliatePartner: boolean | null
  memberInfo: MemberInfo | null
  signInWithToken: (customToken: string) => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | null>(null)

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [isAffiliatePartner, setIsAffiliatePartner] = useState<boolean | null>(null)
  const [memberInfo, setMemberInfo] = useState<MemberInfo | null>(null)

  useEffect(() => {
    let unsubscribe: (() => void) | undefined

    const init = async () => {
      try {
        await initializeFirebase()
        const auth = getFirebaseAuth()

        api.setTokenGetter(async (forceRefresh: boolean) => {
          const currentUser = auth.currentUser
          if (!currentUser) return null
          return currentUser.getIdToken(forceRefresh)
        })

        unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
          setUser(firebaseUser)
          setLoading(false)

          if (!firebaseUser) {
            setIsAffiliatePartner(null)
            setMemberInfo(null)
          }
        })
      } catch (error) {
        console.error('Firebase initialization failed:', error)
        setLoading(false)
      }
    }

    init()

    return () => {
      unsubscribe?.()
    }
  }, [])

  useEffect(() => {
    if (!user) return

    let cancelled = false

    const checkAffiliateStatus = async () => {
      try {
        const response = await api.get<{
          isAffiliatePartner: boolean
          firstName: string
          lastName: string
          phoneNumber: string
        }>('/members/profile')
        if (cancelled) return
        setIsAffiliatePartner(response.data.isAffiliatePartner)
        setMemberInfo({
          firstName: response.data.firstName,
          lastName: response.data.lastName,
          phoneNumber: response.data.phoneNumber,
        })
      } catch (error) {
        if (cancelled) return
        console.error('Failed to check affiliate status:', error)
      }
    }

    checkAffiliateStatus()

    return () => {
      cancelled = true
    }
  }, [user?.uid])

  const signInWithToken = useCallback(async (customToken: string) => {
    const auth = getFirebaseAuth()
    await signInWithCustomToken(auth, customToken)
  }, [])

  const logout = useCallback(async () => {
    const auth = getFirebaseAuth()
    await signOut(auth)
    setUser(null)
    setIsAffiliatePartner(null)
    setMemberInfo(null)
  }, [])

  return (
    <AuthContext.Provider value={{ user, loading, isAffiliatePartner, memberInfo, signInWithToken, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
